import React, { useEffect, useRef, useState } from "react";
import { Container, Box, Grid, Typography, TextField } from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import HeaderTitle from "../common/HeaderTitle";
import { useTranslation } from "react-i18next";
import { commonActions } from "../../redux/features/common/commonSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import OrderReport from "./OrderReport";
import ProductReport from "./ProductReport";
import CommonButton from "../common/Button";
import { subDays, subWeeks, subMonths, subYears, addDays, isSameDay } from "date-fns";
import { Dayjs } from "dayjs";
import BusinessReportDataApi from "./services/businessReportService";
import Utility from "../../utils/Utility";
import { ReportCardSummaryType, ReportCardSummaryTypeWithLogsType } from "../../interfaces/businessReportInterface";
import SimpleReactValidator from "simple-react-validator";
import CustomDateRangePicker from "../common/DateRangePicker";

const BusinessReport = (props: any) => {
    const { showSnackbar, handleLoader } = props;
    const [t] = useTranslation();
    const utility = new Utility();
    const dispatch = useAppDispatch();
    const page = useAppSelector((state) => state?.common?.isTabUrl);
    const [value, setValue] = useState<string>("1");
    const [activeViewBy, setActiveViewBy] = useState<string>("daily");
    const [activeSelectDate, setActiveSelectDate] = useState<string>("1_month");
    const [valueFrom, setValueFrom] = useState<Date | null>(null);
    const [valueTo, setValueTo] = useState<Date | null>(null);
    const [reportCardData, setReportCardData]= useState<ReportCardSummaryType[]>([]);
    const startDate = valueFrom ? utility.getFormattedDateTime(valueFrom) : null;
    const endDate = valueTo ? utility.getFormattedDateTime(valueTo) : null;
    const [, forceUpdate] = useState(0);
    const validator = useRef(
        new SimpleReactValidator({
            autoForceUpdate: { forceUpdate },
            className: "text-danger",
            messages: {             
                required: t("required.this_field_cannot_be_empty"),
            },
        })
    );
    const summaryData = ["受注数", "受注額", "平均受注額", "商品実売点数", "平均商品実売点数"];

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
        dispatch(commonActions.IS_TAB_URL(newValue));
    };

    useEffect(() => {
        if (page !== undefined) {
            setValue(page);
        }
    }, [page]);

    useEffect(() => {
        if (!startDate || !endDate) return;
        reportSummaryDataApiCall(startDate, endDate);
    }, [startDate, endDate,activeViewBy]);

    const handleDateFromChange = (newValue: Date | null) => {
        setValueFrom(newValue);
        if(!newValue){
            validator.current.showMessages();
        } else {
            validator.current.hideMessageFor("from");
            setActiveSelectDate("");
        }
    };

    const handleDateToChange = (newValue: Date | null) => {
        setValueTo(newValue);
        if(!newValue){
            validator.current.showMessages();
        } else {
            validator.current.hideMessageFor("to");
            setActiveSelectDate("");
        }
    };

    const handleCustomDateRangeChange = (from: Date | null, to: Date | null) => {
        setValueFrom(from);
        setValueTo(to);
        setActiveSelectDate("");

        if (from) {
            validator.current.hideMessageFor("from");
        }

        if (to) {
            validator.current.hideMessageFor("to");
        }
    };

    const setDateRangeByKey = (key: string) => {
        const toDate = subDays(new Date(), 1);
        let fromDate;
        validator.current.hideMessages();
        switch (key) {
        case "1_week": {
            fromDate = subDays(toDate, 6);
            break;
        }
        case "1_month":
            fromDate = addDays(subMonths(toDate, 1), 1);
            break;
        case "3_months":
            fromDate = addDays(subMonths(toDate, 3), 1);
            break;
        case "6_months":
            fromDate = addDays(subMonths(toDate, 6), 1);
            break;
        case "1_year":
            fromDate = addDays(subYears(toDate, 1), 1);
            break;
        default:
            fromDate = addDays(subMonths(toDate, 1), 1);
        }

        setValueFrom(fromDate);
        setValueTo(toDate);
        setActiveSelectDate(key);
    };

    useEffect(() => {
        setDateRangeByKey("1_month");
    }, []);

    const isCriteriaMatched = (key: string) => {
        if (!valueTo || !valueFrom) {
            return false;
        }

        switch (key) {
        case "1_week": {
            const expectedFrom = subDays(valueTo, 6);
            return isSameDay(valueFrom, expectedFrom);
        }
        case "1_month": {
            const expectedFrom = addDays(subMonths(valueTo, 1), 1);
            return isSameDay(valueFrom, expectedFrom);
        }
        case "3_months": {
            const expectedFrom = addDays(subMonths(valueTo, 3), 1);
            return isSameDay(valueFrom, expectedFrom);
        }
        case "6_months": {
            const expectedFrom = addDays(subMonths(valueTo, 6), 1);
            return isSameDay(valueFrom, expectedFrom);
        }
        case "1_year": {
            const expectedFrom = addDays(subYears(valueTo, 1), 1);
            return isSameDay(valueFrom, expectedFrom);
        }
        default:
            return false;
        }
    };

    const viewByClass = (key: string) =>
        `business_report_btn${activeViewBy === key ? " active" : ""}`;

    const selectDateClass = (key: string) =>
        `business_report_btn${isCriteriaMatched(key) ? " active" : ""}`;

    const reportSummaryDataApiCall = (startDate:string | null, endDate:string | null) => {
        setReportCardData([]);
        const payload = {};
        const isHitDummyURL = false;
        new BusinessReportDataApi(payload, isHitDummyURL)
            .reportCardSummaryData(startDate, endDate,activeViewBy)
            .then((res:ReportCardSummaryTypeWithLogsType) => {
                setReportCardData(res?.ReportCardSummaryLogs);
            })
            .catch((err:any) => {
                showSnackbar(err?.message, false);
            });
    };

    

    return (
        <Box sx={{ px: 3 }}>
            <HeaderTitle title={t("side_menu.business_report")} />

            <Container>
                <Grid container spacing={2} sx={{ mb:2}} alignItems="center">
                    <Grid item>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography sx={{ mr: 1, color: "#666" }}>{t("business_report.view_by")}:</Typography>
                            <CommonButton route={window.location.pathname} functionToExecute={() => setActiveViewBy("daily")} className={viewByClass("daily")} btnName={t("business_report.daily")} />
                            <CommonButton route={window.location.pathname} functionToExecute={() => setActiveViewBy("weekly")} className={viewByClass("weekly")} btnName={t("business_report.weekly")} />
                            <CommonButton route={window.location.pathname} functionToExecute={() => setActiveViewBy("monthly")} className={viewByClass("monthly")} btnName={t("business_report.monthly")} />
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography sx={{ mr: 1, color: "#666" }}>{t("business_report.select_date")}:</Typography>
                            <CommonButton route={window.location.pathname} functionToExecute={() => setDateRangeByKey("1_week")} className={selectDateClass("1_week")} btnName={t("business_report.1_week")} />
                            <CommonButton route={window.location.pathname} functionToExecute={() => setDateRangeByKey("1_month")} className={selectDateClass("1_month")} btnName={t("business_report.1_month")} />
                            <CommonButton route={window.location.pathname} functionToExecute={() => setDateRangeByKey("3_months")} className={selectDateClass("3_months")} btnName={t("business_report.3_months")} />
                            <CommonButton route={window.location.pathname} functionToExecute={() => setDateRangeByKey("6_months")} className={selectDateClass("6_months")} btnName={t("business_report.6_months")} />
                            <CommonButton route={window.location.pathname} functionToExecute={() => setDateRangeByKey("1_year")} className={selectDateClass("1_year")} btnName={t("business_report.1_year")} />
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={2} className="business_report_summary">
                    {/* Selected Date Card */}
                    <Grid item xs={12} sm={12} md={12} lg={2} className="report_card date_range_card">
                        <Box sx={{width:"100%"}}>
                            <Typography className="report_card_title">{t("business_report.select_date")}</Typography>
                            <Box mt={2}>
                                <CustomDateRangePicker
                                    valueFrom={valueFrom}
                                    valueTo={valueTo}
                                    onDateRangeChange={handleCustomDateRangeChange}
                                    onDateFromChange={handleDateFromChange}
                                    onDateToChange={handleDateToChange}
                                    showSnackbar={showSnackbar}
                                />
                                {validator.current.message("from", valueFrom, ["required"])}
                                {validator.current.message("to", valueTo, ["required"])}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Metric Cards - Mock Values matching the image */}
                    {reportCardData?.length >= 1 ?
                        reportCardData?.map((item: any, index: number) => (
                            <Grid key={index} className="report_card">
                                <Box>
                                    <Typography className="report_card_title">{item?.title}</Typography>
                                    <Typography className="report_card_value" pt={2}>{new Utility().numberWithCommas(item?.value)} </Typography>
                                </Box>
                            </Grid>
                        )):                    
                        summaryData?.map((item: any, index: number) => (
                            <Grid key={index} className="report_card">
                                <Box>
                                    <Typography className="report_card_title">{item}</Typography>
                                    <Typography className="report_card_value" pt={2}>{"-"} </Typography>
                                </Box>
                            </Grid>
                        ))}
                </Grid>
            </Container>



            {/* table content starts */}
            <Box>
                <TabContext value={value}>
                    {/*Tab menu starts here*/}
                    <Container sx={{px:1}}>
                        <Grid container spacing={0}>
                            <Grid item xs={12} sm={12} md={12} lg={12}>
                                <Box>
                                    <TabList
                                        onChange={handleChange}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        aria-label="scrollable auto tabs example"
                                    >
                                        <Tab
                                            iconPosition="start"
                                            label={t("business_report.order_report")}
                                            value="1"
                                            className="oms_tab"
                                        />
                                        <Tab
                                            iconPosition="start"
                                            label={t("business_report.product_report")}
                                            value="2"
                                            className="oms_tab"
                                        />
                                    </TabList>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>

                    {/*Tab menu ends here*/}
                    <TabPanel value="1">
                        <OrderReport
                            handleLoader={handleLoader}
                            showSnackbar={showSnackbar}
                            valueFrom={valueFrom}
                            valueTo={valueTo}
                            activeViewBy={activeViewBy}
                        />
                    </TabPanel>

                    <TabPanel value="2">
                        <ProductReport
                            handleLoader={handleLoader}
                            showSnackbar={showSnackbar}
                            valueFrom={valueFrom}
                            valueTo={valueTo}
                            activeViewBy={activeViewBy}
                        />
                    </TabPanel>
                </TabContext>
            </Box>
        </Box>
    );
};

export default BusinessReport;
